import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import {getFlag} from 'components/cabinet/calculator/components/delivery/helpers';
import {useCalculationReadOnly} from 'components/cabinet/calculator/calculation-read-only';
import {useInformers} from 'api/informers';
import {COLORS, INFORMERS_CODES} from 'config/constants';
import {numberWithSpaces} from 'utils/helpers';
import CustomsProperty from './property';

import './code-properties.scss';

const CustomsCodeProperties = ({declaration, vatRate, showVat, onChooseVat}: CustomsCodePropertiesProps) => {
  const {t} = useTranslation();

  const informers = useInformers();
  const readOnly = useCalculationReadOnly();

  const importTax = declaration.import_tax?.description || `${declaration.import_tax?.value}%`;
  const vatLabel = typeof vatRate === 'number' ? `${vatRate}%` : vatRate?.Value || '';
  const countryCode = declaration.country_data.code?.toUpperCase();

  const vatValue = (
    <Typography
      component='button'
      type='button'
      variant='inherit'
      disabled={readOnly}
      onClick={onChooseVat}
      sx={{
        padding: 0,
        border: 0,
        textDecoration: 'underline dotted',
        textUnderlineOffset: '3px',
        background: 'transparent',
        color: readOnly ? COLORS.BLACK_DARK : COLORS.BLUE,
        cursor: 'pointer',
        lineHeight: '21px',
        '&:hover': {
          color: COLORS.BLUE_DARK,
        },
      }}
    >
      {vatLabel}
    </Typography>
  );

  return (
    <div className='customs-code-properties'>
      <CustomsProperty
        label={t('calc__text__packaging__nameItem')}
        value={Array.from(new Set(declaration.product_names)).join(', ')}
      />
      <CustomsProperty
        label={t('calc__text__customs__originCountry')}
        value={
          <span className='customs-code-properties__country'>
            {getFlag({
              code: countryCode,
              width: 'auto',
              height: '14px',
              marginRight: '0',
            })}
            {declaration.country_data.name}
          </span>
        }
      />
      <CustomsProperty
        label={t('calc__text__customs__customsCost')}
        informer={informers?.[INFORMERS_CODES.CUSTOMS_COST]}
        value={`${numberWithSpaces(declaration.lowin)} USD`}
      />
      <CustomsProperty
        nested
        secondary
        label={t('common__text__invoice')}
        value={`${numberWithSpaces(declaration.invc)} USD`}
      />
      <CustomsProperty
        nested
        secondary
        label={t('calc__text__customs__deliveryToCustoms')}
        value={`${numberWithSpaces(declaration.delivery2customs)} USD`}
      />
      <CustomsProperty label={t('calc__text__customs__dutyRate')} value={importTax} />
      <CustomsProperty
        label={t('calc__text__customs__dutyAmount')}
        value={`${numberWithSpaces(declaration.duty)} USD`}
      />
      {Boolean(declaration.excise) && (
        <CustomsProperty
          showInformer
          label={t('calc__text__customs__excise')}
          informer={informers?.[INFORMERS_CODES.EXCISE]}
          value={`${numberWithSpaces(declaration.excise)} USD`}
        />
      )}
      {Boolean(declaration.utilization_fee) && (
        <CustomsProperty
          label={t('calc__text__customs__utilizationFee')}
          value={`${numberWithSpaces(declaration.utilization_fee)} USD`}
        />
      )}
      {Boolean(declaration.environmental_fee) && (
        <CustomsProperty
          label={t('calc__text__customs__environmentalFee')}
          value={`${numberWithSpaces(declaration.environmental_fee)} USD`}
        />
      )}
      {showVat && <>
        <CustomsProperty label={t('common__text__vatRate')} value={vatValue} />
        <CustomsProperty
          label={t('calc__text__customs__vatAmount')}
          value={`${numberWithSpaces(declaration.vat)} USD`}
        />
      </>}
    </div>
  );
};

export default CustomsCodeProperties;
